import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiCalendar,
    FiArrowLeft,
    FiCheckCircle,
    FiMail,
    FiInfo,
    FiSend,
    FiChevronRight,
    FiHome,
} from "react-icons/fi";
import publicLeaveService from "../../services/publicLeaveService";

const STEPS = ["College", "Employee", "Verify OTP", "Leave Details"];

const computeInclusiveDays = (from, to) => {
    if (!from || !to) return 0;
    const f = new Date(`${from}T00:00:00.000Z`);
    const t = new Date(`${to}T00:00:00.000Z`);
    const diffMs = t.getTime() - f.getTime();
    if (diffMs < 0) return 0;
    return Math.round(diffMs / 86400000) + 1;
};

export default function PublicLeaveApplication() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);

    const [colleges, setColleges] = useState([]);
    const [selectedCollege, setSelectedCollege] = useState(null);

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [maskedEmail, setMaskedEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [verificationToken, setVerificationToken] = useState("");

    const [leaveTypes, setLeaveTypes] = useState([]);
    const [form, setForm] = useState({ leaveType: "", fromDate: "", toDate: "", reason: "" });
    const [submitted, setSubmitted] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Cooldown timer effect
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    // ── Step 0: Load Colleges ────────────────────────────────────────────
    useEffect(() => {
        publicLeaveService
            .getColleges()
            .then((res) => {
                const list = res.data?.data ?? res.data ?? [];
                setColleges(list);

                // If college query param exists in URL, pre-select it
                const collegeParam = searchParams.get("college");
                if (collegeParam) {
                    const match = list.find((c) => c._id === collegeParam || c.code === collegeParam);
                    if (match) {
                        pickCollege(match);
                    }
                } else if (list.length === 1) {
                    // Single college deployment convenience
                    pickCollege(list[0]);
                }
            })
            .catch(() => toast.error("Failed to load colleges"));
    }, [searchParams]);

    const pickCollege = async (college) => {
        setSelectedCollege(college);
        setLoading(true);
        try {
            const res = await publicLeaveService.getEmployees(college._id);
            const emps = res.data?.data ?? res.data ?? [];
            setEmployees(emps);
            setStep(1);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to load staff list");
        } finally {
            setLoading(false);
        }
    };

    const pickEmployee = async (employee) => {
        setSelectedEmployee(employee);
        setLoading(true);
        try {
            const res = await publicLeaveService.requestOtp(employee._id);
            const masked = res.data?.maskedEmail || res.data?.data?.maskedEmail || res.maskedEmail || "";
            setMaskedEmail(masked);
            setResendCooldown(60);
            setStep(2);
            toast.success("Verification code sent to your email");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to send verification code");
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendCooldown > 0 || !selectedEmployee) return;
        setLoading(true);
        try {
            const res = await publicLeaveService.requestOtp(selectedEmployee._id);
            const masked = res.data?.maskedEmail || res.data?.data?.maskedEmail || res.maskedEmail || "";
            setMaskedEmail(masked);
            setResendCooldown(60);
            toast.success("New verification code sent");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to resend code");
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async (e) => {
        e?.preventDefault();
        if (!otp.trim() || otp.trim().length < 6) {
            return toast.error("Please enter the complete 6-digit verification code");
        }
        setLoading(true);
        try {
            const res = await publicLeaveService.verifyOtp(selectedEmployee._id, otp.trim());
            const token = res.data?.verificationToken || res.data?.data?.verificationToken || res.verificationToken;
            if (!token) {
                throw new Error("Verification token was not returned from the server.");
            }
            setVerificationToken(token);

            const typesRes = await publicLeaveService.getLeaveTypes(selectedEmployee._id, token);
            const types = typesRes.data?.data ?? typesRes.data ?? typesRes ?? [];
            setLeaveTypes(Array.isArray(types) ? types : []);
            if (Array.isArray(types) && types.length > 0) {
                setForm((prev) => ({ ...prev, leaveType: types[0]._id }));
            }
            setStep(3);
            toast.success("Identity verified!");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Invalid or expired verification code");
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const totalDaysRequested = useMemo(() => {
        return computeInclusiveDays(form.fromDate, form.toDate);
    }, [form.fromDate, form.toDate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.leaveType) return toast.error("Select a leave type");
        if (!form.fromDate || !form.toDate) return toast.error("Select both from and to dates");
        if (form.toDate < form.fromDate) return toast.error("To date cannot be earlier than from date");
        if (!form.reason.trim()) return toast.error("Please enter the reason for leave");

        setLoading(true);
        try {
            const res = await publicLeaveService.submit({
                employeeId: selectedEmployee._id,
                token: verificationToken,
                ...form,
                reason: form.reason.trim(),
            });

            if (res.data?.warning) {
                toast(res.data.warning, { icon: "⚠️", duration: 6000 });
            }
            toast.success("Leave application submitted successfully!");
            setSubmitted(true);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to submit leave application");
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => setStep((s) => Math.max(0, s - 1));

    return (
        <div style={{
            minHeight: "100vh",
            background: "linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%)",
            padding: "2.5rem 1rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        }}>
            <div style={{ width: "100%", maxWidth: 520 }}>
                {/* Header Branding */}
                <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
                    <div style={{
                        width: 54, height: 54, borderRadius: 14,
                        background: "linear-gradient(135deg, #003b67, #002747)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        margin: "0 auto 0.85rem",
                        boxShadow: "0 8px 16px rgba(0, 59, 103,0.25)",
                    }}>
                        <FiCalendar size={26} color="#fff" />
                    </div>
                    <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#1e293b" }}>
                        Staff Leave Application
                    </h1>
                    <p style={{ fontSize: 13, color: "var(--text-muted, #64748b)", margin: "4px 0 0" }}>
                        Self-service leave portal for non-teaching staff (No password required)
                    </p>
                </div>

                {!submitted && step > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={goBack}
                            style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                        >
                            <FiArrowLeft size={13} /> Back
                        </button>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>
                            Step {step + 1} of {STEPS.length}: {STEPS[step]}
                        </span>
                    </div>
                )}

                {submitted ? (
                    <div className="card" style={{ padding: "2.5rem 2rem", textAlign: "center", animation: "fadeIn 0.3s ease" }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: "50%",
                            background: "#dcfce7", color: "#15803d",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            margin: "0 auto 1rem",
                        }}>
                            <FiCheckCircle size={36} />
                        </div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px", color: "#15803d" }}>
                            Application Submitted!
                        </h2>
                        <p style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.5, marginBottom: "1.5rem" }}>
                            Your leave request has been submitted to <strong>{selectedCollege?.name}</strong> administration for review and approval. You will receive departmental notification upon approval.
                        </p>
                        <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: 8, textAlign: "left", fontSize: 13, marginBottom: "1.5rem" }}>
                            <div><strong>Staff:</strong> {selectedEmployee?.fullName} ({selectedEmployee?.department})</div>
                            <div><strong>Dates:</strong> {form.fromDate} to {form.toDate} ({totalDaysRequested} days)</div>
                            <div><strong>Reason:</strong> {form.reason}</div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ width: "100%", justifyContent: "center" }}
                            onClick={() => {
                                setSubmitted(false);
                                setStep(colleges.length > 1 ? 0 : 1);
                                setOtp("");
                                setVerificationToken("");
                                setForm({ leaveType: "", fromDate: "", toDate: "", reason: "" });
                            }}
                        >
                            Submit Another Request
                        </button>
                    </div>
                ) : (
                    <div className="card" style={{ padding: "1.75rem", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
                        {/* Step 0: College */}
                        {step === 0 && (
                            <div>
                                <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>Step 1: Select Your College</h2>
                                <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "1rem" }}>
                                    Choose the institution where you are currently employed:
                                </p>
                                <div style={{ display: "grid", gap: 8 }}>
                                    {colleges.map((c) => (
                                        <button
                                            key={c._id}
                                            type="button"
                                            className="btn btn-outline"
                                            style={{
                                                justifyContent: "space-between",
                                                textAlign: "left",
                                                padding: "0.85rem 1rem",
                                                borderRadius: 8,
                                                borderColor: "#cbd5e1",
                                            }}
                                            onClick={() => pickCollege(c)}
                                            disabled={loading}
                                        >
                                            <span style={{ fontWeight: 600 }}>{c.name}</span>
                                            <FiChevronRight size={16} color="#94a3b8" />
                                        </button>
                                    ))}
                                    {colleges.length === 0 && (
                                        <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", padding: "1rem" }}>
                                            No colleges found. Please check back later.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 1: Employee */}
                        {step === 1 && (
                            <div>
                                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "6px 12px", borderRadius: 6, fontSize: 12, color: "#166534", marginBottom: 14 }}>
                                    <strong>College:</strong> {selectedCollege?.name}
                                </div>
                                <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>Step 2: Find Your Name</h2>
                                <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "1rem" }}>
                                    Select your name from the staff registry. A 6-digit OTP will be sent to your registered email for verification.
                                </p>

                                <div className="form-group">
                                    <label className="form-label">Staff Member Name <span style={{ color: "var(--danger)" }}>*</span></label>
                                    <select
                                        className="form-control"
                                        defaultValue=""
                                        onChange={(e) => {
                                            const emp = employees.find((x) => x._id === e.target.value);
                                            if (emp) pickEmployee(emp);
                                        }}
                                        disabled={loading}
                                        style={{ fontSize: 14, padding: "8px 12px" }}
                                    >
                                        <option value="" disabled>Select your name…</option>
                                        {employees.map((emp) => (
                                            <option key={emp._id} value={emp._id}>
                                                {emp.fullName} ({emp.employeeCode}) — {emp.department || emp.designation || "Staff"}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {employees.length === 0 && (
                                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8 }}>
                                        No non-teaching staff found for this college. Please contact the administrator.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Step 2: OTP Verification */}
                        {step === 2 && (
                            <form onSubmit={verifyOtp}>
                                <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
                                    <div style={{
                                        width: 48, height: 48, borderRadius: "50%",
                                        background: "#e8f1f8", color: "#003b67",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        margin: "0 auto 0.75rem",
                                    }}>
                                        <FiMail size={22} />
                                    </div>
                                    <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>Email Verification</h2>
                                    <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
                                        We sent a 6-digit verification code to<br />
                                        <strong style={{ color: "var(--text)" }}>{maskedEmail || "your registered email address"}</strong>
                                    </p>
                                </div>

                                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                                    <label className="form-label" style={{ textAlign: "center", display: "block" }}>
                                        Enter 6-Digit Code
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        placeholder="000000"
                                        style={{ fontSize: 24, letterSpacing: 8, textAlign: "center", fontWeight: 700 }}
                                        maxLength={6}
                                        autoFocus
                                        required
                                    />
                                    <small className="form-hint" style={{ textAlign: "center" }}>
                                        Code expires in 10 minutes
                                    </small>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ width: "100%", justifyContent: "center", padding: "0.65rem" }}
                                    disabled={loading || otp.length < 6}
                                >
                                    {loading ? "Verifying…" : "Verify & Continue"}
                                </button>

                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12.5 }}>
                                    <span style={{ color: "var(--text-muted)" }}>Didn't receive the code?</span>
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={loading || resendCooldown > 0}
                                        style={{
                                            background: "none", border: "none",
                                            color: resendCooldown > 0 ? "var(--text-muted)" : "var(--primary)",
                                            fontWeight: 600, cursor: resendCooldown > 0 ? "default" : "pointer",
                                            padding: 0,
                                        }}
                                    >
                                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Code"}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Step 3: Application Form */}
                        {step === 3 && (
                            <form onSubmit={submit}>
                                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginBottom: 14 }}>
                                    <div><strong>Applicant:</strong> {selectedEmployee?.fullName} ({selectedEmployee?.employeeCode})</div>
                                    <div style={{ color: "#166534" }}>{selectedEmployee?.department} · {selectedEmployee?.designation || "Non Teaching"}</div>
                                </div>

                                <div className="form-group" style={{ marginBottom: "1rem" }}>
                                    <label className="form-label">
                                        Type of Leave <span style={{ color: "var(--danger)" }}>*</span>
                                    </label>
                                    <select
                                        name="leaveType"
                                        value={form.leaveType}
                                        onChange={handleFormChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="" disabled>Select leave type…</option>
                                        {leaveTypes.map((lt) => (
                                            <option key={lt._id} value={lt._id}>
                                                {lt.name} (Quota: {lt.allowedPerYear} days/yr)
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                    <div className="form-group">
                                        <label className="form-label">
                                            From Date <span style={{ color: "var(--danger)" }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="fromDate"
                                            value={form.fromDate}
                                            onChange={handleFormChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">
                                            To Date <span style={{ color: "var(--danger)" }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="toDate"
                                            value={form.toDate}
                                            min={form.fromDate || undefined}
                                            onChange={handleFormChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>
                                </div>

                                {totalDaysRequested > 0 && (
                                    <div style={{
                                        padding: "6px 10px",
                                        borderRadius: 6,
                                        background: "#e8f1f8",
                                        border: "1px solid #b6d5ec",
                                        color: "#1e40af",
                                        fontSize: 12.5,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: "1rem",
                                    }}>
                                        <FiInfo size={14} /> Total Duration: <strong>{totalDaysRequested} day{totalDaysRequested !== 1 ? "s" : ""}</strong>
                                    </div>
                                )}

                                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                                    <label className="form-label">
                                        Reason for Leave <span style={{ color: "var(--danger)" }}>*</span>
                                    </label>
                                    <textarea
                                        name="reason"
                                        value={form.reason}
                                        onChange={handleFormChange}
                                        rows={3}
                                        className="form-control"
                                        placeholder="Please provide details for your leave request"
                                        required
                                        maxLength={500}
                                    />
                                    <small className="form-hint" style={{ textAlign: "right" }}>
                                        {form.reason.length}/500
                                    </small>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ width: "100%", justifyContent: "center", padding: "0.65rem", display: "flex", alignItems: "center", gap: 6 }}
                                    disabled={loading}
                                >
                                    <FiSend size={14} /> {loading ? "Submitting Application…" : "Submit Leave Application"}
                                </button>
                            </form>
                        )}
                    </div>
                )}

                {/* Progress Dots */}
                {!submitted && (
                    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 18 }}>
                        {STEPS.map((label, i) => (
                            <span
                                key={label}
                                title={label}
                                style={{
                                    width: i === step ? 22 : 8,
                                    height: 8,
                                    borderRadius: 99,
                                    background: i === step ? "#003b67" : i < step ? "#93c5fd" : "#cbd5e1",
                                    transition: "all 0.25s ease",
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* Quick Link back to Login */}
                <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                    <button
                        type="button"
                        onClick={() => navigate("/login")}
                        style={{
                            background: "none", border: "none", color: "var(--text-muted)",
                            fontSize: 12.5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
                        }}
                    >
                        <FiHome size={13} /> Return to Portal Sign In
                    </button>
                </div>
            </div>
        </div>
    );
}